import { useState, FC, MouseEvent, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';

import { Box, ClickAwayListener, Paper, Popper, styled, TextField } from '@mui/material';
import { DateRange } from '@mui/icons-material';
import { StaticDatePicker } from '@mui/x-date-pickers';

import { useBreakpoints } from '@frontend/features/common/hooks/useBreakpoints';

dayjs.extend(localizedFormat);

interface RoomCalendarProps {
  identifier: 'checkIn' | 'checkOut';
  onChange: (value: Date) => void;
  value: Date | null
  minDate?: Date | null
  maxDate?: Date | null
}

const DateRangeIcon = styled(DateRange)(({ theme }) => ({
  color: theme.palette.primary.main,
  position: 'relative',
  left: theme.spacing(-0.5),
  marginRight: theme.spacing(0.5),
}));

export const HotelSearchCalendar: FC<RoomCalendarProps> = ({identifier, onChange, value, minDate, maxDate}) => {
  const { t } = useTranslation();
  const [ anchorEl, setAnchorEl ] = useState<HTMLDivElement | null>(null);
  const { isMediumUp } = useBreakpoints();
  const [inputValue, setInputValue] = useState('');

  const handleClickAway = () => {
    setAnchorEl(null);
  };

  const handleTextFieldClick = (e: MouseEvent<HTMLDivElement>) => {
    setAnchorEl(anchorEl ? null : e.currentTarget);
  };

  const handleDateChange = (date: Dayjs | null) => {
    if (date) {
      onChange(date.toDate());
      setAnchorEl(null);
    }
  };

  useEffect(() => {
    if(value) {
      setInputValue(dayjs(value).format('DD-MM-YYYY'));
    }
  }, [value]);
  
  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box sx={{
        width: '100%',
        minWidth: isMediumUp ? 100 : undefined,
        maxWidth: isMediumUp ? 200 : undefined,
      }}
      >
        <TextField
          fullWidth
          value={inputValue || ''}
          size= 'small'
          placeholder= {t(`hotels.searcher.${identifier}`)}
          InputProps={{ 
            startAdornment: <DateRangeIcon/>,
            readOnly: true
          }}
          onClick={handleTextFieldClick}
        />
        <Popper disablePortal open={Boolean(anchorEl)} anchorEl={anchorEl} placement="bottom-start">
          <Paper elevation={8}>
            <StaticDatePicker
              disablePast
              minDate={minDate ? dayjs(minDate) : undefined}
              maxDate={maxDate ? dayjs(maxDate) : undefined}
              value={dayjs(value) || null}
              onChange={handleDateChange}
              slots={{
                toolbar: () => <div></div>,
                actionBar: () => <div></div>,
              }}
              sx={{
                borderRadius: 4
              }}
            />
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

